import { React, useContext } from "react";
import { HiOutlineArrowNarrowRight } from "react-icons/hi";
import { RiCodeSSlashLine, RiRocketLine, RiTerminalBoxLine } from "react-icons/ri";
import bannerImg from "../assets/overlay.jpg"; 
import { IoMdArrowDropright } from "react-icons/io";
import {AppContext} from "../App";

export default function HomeHero() {
  const appContext = useContext(AppContext);  

  
  return (
    <>
      <style>{`
        /* Centralized Structure */
        .hero-viewport {
          position: relative;
          width: 100%;
          min-height: 100vh;
          display: flex;
          align-items: center;
          overflow: hidden;
          font-family: inherit;
        }

        .hero-bg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          z-index: 0;
          transform: scale(1.05);
          animation: slowZoom 20s infinite alternate;
        }

        @keyframes slowZoom {
          from { transform: scale(1); }
          to { transform: scale(1.1); }
        }

        .hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to right,
            rgba(10, 20, 30, 0.8) 0%,
            rgba(10, 20, 30, 0.4) 100%
          );
          z-index: 1;
        }

        .hero-container {
          position: relative;
          z-index: 2;
          width: 100%;
          max-width: 1300px;
          margin: 0 auto;
          padding: 0 40px;
        }

        .hero-card {
          padding: 50px;
          max-width: 800px;
        }

        /* Rich Typography & Transitions */
        .hero-title {
          font-size: clamp(2.8rem, 6.5vw, 5rem);
          font-weight: 900;
          line-height: 1.05;
          letter-spacing: -0.05em;
          margin-bottom: 25px;
          color: #ffffff;
          font-family: 'Inter', system-ui, sans-serif;
        }

        .motto-word {
          display: inline-block;
          overflow: hidden;
          vertical-align: bottom;
        }

        .motto-inner {
          display: inline-block;
          animation: slideUp 0.8s cubic-bezier(0.2, 1, 0.3, 1) forwards;
          transform: translateY(100%);
        }

        .delay-1 { animation-delay: 0.2s; }
        .delay-2 { animation-delay: 0.4s; }

        @keyframes slideUp {
          to { transform: translateY(0); }
        }

        .text-secondary-glow {
          color: var(--text-primary);
          text-shadow: 0 0 20px rgba(var(--secondaryColor), 0.3);
        }

        .hero-body {
          font-size: 1.2rem;
          line-height: 1.7;
          max-width: 580px;
          margin-bottom: 45px;
          color: rgba(255, 255, 255, 0.8);
          animation: fadeIn 1s ease 0.8s forwards;
          opacity: 0;
        }

        @keyframes fadeIn {
          to { opacity: 1; }
        }

        /* Enhanced Rich Buttons */
        .hero-actions {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
        }

        .btn-rich {
          position: relative;
          padding: 18px 36px;
          font-size: 0.85rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 2px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
          overflow: hidden;
          border: none;
          cursor: pointer;
          color: var(--secondaryColor) !important;
          background: rgba(255,255,255,.15) !important;
        }

        .btn-primary-rich {
          background: var(--text-primary);
          color: #fff;
          box-shadow: 0 10px 20px -10px var(--text-primary);
        }

        .btn-primary-rich:hover {
          transform: translateY(-5px) scale(1.02);
          box-shadow: 0 20px 40px -15px var(--text-primary);
          filter: brightness(1.1);
        }

        .btn-outline-rich {
          background: rgba(255, 255, 255, 0.05);
          color: #fff;
          border: 1px solid rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
        }

        .btn-outline-rich:hover {
          background: #fff;
          color: #000;
          border-color: #fff;
          transform: translateY(-5px);
        }

        .icon-move {
          transition: transform 0.3s ease;
        }
        .btn-rich:hover .icon-move {
          transform: translateX(5px);
        }

        .btn-rich:hover{
          color: var(--secondaryColor);
          background: rgba(255,255,255,.075);
        }

        /* Rich Stats */
        .hero-stats {
          display: flex;
          gap: 50px;
          margin-top: 60px;
          padding-top: 40px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .stat-box {
          display: flex;
          align-items: center;
          gap: 20px;
          transition: transform 0.3s ease;
        }
        
        .stat-box:hover {
          transform: translateX(10px);
        }

        .stat-circle {
          width: 50px;
          height: 50px;
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--secondaryColor);
        }

        .stat-val {
          display: block;
          font-size: 1.8rem;
          font-weight: 900;
          color: #fff;
          line-height: 1;
        }

        .stat-label {
          color: rgba(255, 255, 255, 0.5);
          font-size: 0.65rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 2px;
        }

        @media (max-width: 768px) {
          .hero-container { padding: 0 20px; }
          .hero-card { padding: 30px 10px; }
          .hero-actions { justify-content: start; }
          .btn-rich { width: 100%; justify-content: center; }
          .hero-stats { flex-direction: column; gap: 30px; }
        }
      `}</style>

      <section className="hero-viewport">
        <img src={bannerImg} className="hero-bg" alt="Werenium Concept" />
        <div className="hero-overlay" />

        <div className="hero-container pt-5">
          <div className="hero-card">
            <h1 className="hero-title">
              <span className="motto-word">
                <span className="motto-inner">We Code,</span>
              </span>{" "}
              <span className="motto-word">
                <span className="motto-inner text-secondary-glow delay-1 text-secondary">You Grow.</span>
              </span>
            </h1>

            <p className="hero-body">
              Expert software development services crafting high-fidelity 
              solutions that drive business innovation in the digital era.
            </p>

            <div className="hero-actions mDesktopOnly">
              <button className="btn-rich btn-primary-rich" onClick={() => appContext.navTo({item:'contact_us'})}>
                <span>Get Started</span> 
                <IoMdArrowDropright className="icon-move" size={24} />
              </button>
            </div>

            <div className="hero-stats">
              <div className="stat-box">
                <div className="stat-circle">
                  <RiCodeSSlashLine size={24} />
                </div>
                <div>
                  <span className="stat-val">60+</span>
                  <span className="stat-label">Projects</span>
                </div>
              </div>
              <div className="stat-box">
                <div className="stat-circle">
                  <RiRocketLine size={24} />
                </div>
                <div>
                  <span className="stat-val">10K+</span>
                  <span className="stat-label">Users</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}